import { useState, useEffect, useRef } from "react";
import { FaStar, FaStarHalfAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { TestimonialType } from "@/data/testimonials";
import { useIsMobile } from "@/hooks/use-mobile";

interface TestimonialCarouselProps {
  testimonials: TestimonialType[];
}

const TestimonialCarousel = ({ testimonials }: TestimonialCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useIsMobile();
  const carouselRef = useRef<HTMLDivElement>(null);

  // How many items to show per slide based on screen size
  const itemsPerSlide = isMobile ? 1 : window.innerWidth >= 1024 ? 3 : 2;
  const totalSlides = Math.ceil(testimonials.length / itemsPerSlide);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  useEffect(() => {
    if (carouselRef.current) {
      const translateValue = -(currentIndex * 100);
      carouselRef.current.style.transform = `translateX(${translateValue}%)`;
    }
  }, [currentIndex, itemsPerSlide]);

  return (
    <div className="relative testimonials-carousel">
      <div className="overflow-hidden">
        <div 
          ref={carouselRef}
          className="flex transition-transform duration-300"
          style={{ transform: `translateX(0%)` }}
        >
          {Array.from({ length: totalSlides }).map((_, slideIndex) => (
            <div key={slideIndex} className="flex w-full flex-shrink-0">
              {testimonials
                .slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide)
                .map((testimonial) => (
                  <div key={testimonial.id} className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4">
                    <div className="bg-white rounded-lg shadow-md p-6 h-full flex flex-col">
                      <div className="flex items-center mb-4">
                        <div className="text-[#FFC857] flex">
                          {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                            <FaStar key={i} />
                          ))}
                          {testimonial.rating % 1 !== 0 && <FaStarHalfAlt />}
                        </div>
                      </div>
                      <div className="relative pl-8 mb-6 flex-grow">
                        <svg
                          className="absolute top-0 left-0 text-primary/20 w-6 h-6 -translate-y-2"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                        <p className="italic text-gray-700">"{testimonial.text}"</p>
                      </div>
                      <div className="flex items-center border-t pt-4">
                        <div className="text-primary w-12 h-12 flex items-center justify-center mr-4">
                          <svg
                            className="w-8 h-8"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M20.309 17.708C22.196 15.66 22.006 13.03 22 13V5a1 1 0 0 0-1-1h-6c-1.103 0-2 .897-2 2v7a1 1 0 0 0 1 1h3.078a2.89 2.89 0 0 1-.429 1.396c-.508.801-1.465 1.348-2.846 1.624l-.803.16V20h1c2.783 0 4.906-.771 6.309-2.292zm-11.007 0C11.19 15.66 10.999 13.03 10.993 13V5a1 1 0 0 0-1-1h-6c-1.103 0-2 .897-2 2v7a1 1 0 0 0 1 1h3.078a2.89 2.89 0 0 1-.429 1.396c-.508.801-1.465 1.348-2.846 1.624l-.803.16V20h1c2.783 0 4.906-.771 6.309-2.292z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-heading font-bold">{testimonial.name}</h4>
                          <p className="text-sm text-gray-600">{testimonial.location}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      <button
        className="absolute top-1/2 left-0 -translate-y-1/2 bg-white p-2 rounded-full shadow-md z-10 hover:bg-gray-100 focus:outline-none md:-left-5"
        onClick={goToPrevious}
        aria-label="Предишен отзив"
      >
        <FaChevronLeft className="text-primary" />
      </button>

      <button
        className="absolute top-1/2 right-0 -translate-y-1/2 bg-white p-2 rounded-full shadow-md z-10 hover:bg-gray-100 focus:outline-none md:-right-5"
        onClick={goToNext}
        aria-label="Следващ отзив"
      >
        <FaChevronRight className="text-primary" />
      </button>

      <div className="flex justify-center mt-8">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            className={`w-3 h-3 rounded-full mx-1 ${
              currentIndex === index ? "bg-primary" : "bg-gray-300"
            }`}
            onClick={() => goToSlide(index)}
            aria-label={`Отиди на отзив ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialCarousel;
